import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import CurrencyConverter from '../../CurrencyConverter';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import { OptionIcon } from '../../ListingOptions/optionIcons';
import messages from '../../../locale/jsonMessages';
import { OPTION_LIMITS } from '../../../helpers/bookingOptionsBilling';
import {
  getOptionLabel, getOptionDescription, getBillingModeMessage, isDeliveryLocationValid,
} from '../../../helpers/bookingOptionsPresentation';
import s from './BookingOptions.css';

const TOOLTIPS = { delivery: messages.boDeliveryInfo, chauffeur: messages.boChauffeurInfo };

// "Options et services" panel of the booking form: one checkbox per bookable option, none ticked by default.
// The delivery location is required (5 to 255 characters) while the delivery option is ticked.
function BookingOptions({ options, selectedIds, onToggle, deliveryLocation, onLocationChange, currency, disabled }) {
  const { formatMessage } = useIntl();
  if (!Array.isArray(options) || options.length === 0) return null;
  const selected = new Set((selectedIds || []).map(Number));
  const deliverySelected = options.some((option) => option.code === 'delivery' && selected.has(Number(option.id)));
  const locationInvalid = deliverySelected && !isDeliveryLocationValid(deliveryLocation);
  const locationTouched = typeof deliveryLocation === 'string' && deliveryLocation.length > 0;

  return (
    <section className={s.panel} aria-labelledby="booking-options-title">
      <header className={s.header}>
        <h4 id="booking-options-title" className={s.title}>{formatMessage(messages.boTitle)}</h4>
        <p className={s.subtitle}>{formatMessage(messages.boRenterSubtitle)}</p>
      </header>
      <ul className={s.list}>
        {options.map((option) => {
          const checked = selected.has(Number(option.id));
          const inputId = `booking-option-${option.id}`;
          const label = getOptionLabel(option, formatMessage);
          const description = getOptionDescription(option, formatMessage);
          const extra = TOOLTIPS[option.code] ? formatMessage(TOOLTIPS[option.code]) : '';
          const hasTip = Boolean(description || extra);
          const row = (
            <li className={cx(s.item, checked && s.itemChecked)}>
              <input id={inputId} type="checkbox" className={s.checkbox} checked={checked} disabled={disabled}
                onChange={(event) => onToggle(Number(option.id), event.target.checked)} />
              <span className={s.icon}><OptionIcon name={option.icon || (option.isFixed ? option.code : 'star')} size={18} /></span>
              <label htmlFor={inputId} className={s.text}>
                <span className={s.label}>{label}</span>
              </label>
              {hasTip && <span className={s.info} aria-hidden="true"><span className="glyphicon glyphicon-info-sign" /></span>}
              <span className={s.price}>
                <strong><CurrencyConverter amount={Number(option.price)} from={currency} /></strong>
                <small>({formatMessage(getBillingModeMessage(option.billingMode, { short: true }))})</small>
              </span>
            </li>
          );
          if (!hasTip) return React.cloneElement(row, { key: option.id });
          // The description is shown on hover or keyboard focus of the whole option, not inline.
          const overlay = (
            <Popover id={`booking-option-tip-${option.id}`} className="customTooltipContainer">
              <span className={s.tipTitle}>{label}</span>
              {description && <span className={s.tipText}>{description}</span>}
              {extra && <span className={s.tipText}>{extra}</span>}
            </Popover>
          );
          return <OverlayTrigger key={option.id} placement="top" overlay={overlay}>{row}</OverlayTrigger>;
        })}
      </ul>
      {deliverySelected && (
        <div className={s.location}>
          <label htmlFor="booking-delivery-location">
            {formatMessage(messages.boDeliveryLocation)}<sup className="isRequiredStar">*</sup>
          </label>
          <input id="booking-delivery-location" type="text" className="form-control" value={deliveryLocation || ''}
            maxLength={OPTION_LIMITS.deliveryLocationMax} autoComplete="off"
            placeholder={formatMessage(messages.boDeliveryLocationPlaceholder)}
            aria-invalid={locationInvalid} aria-describedby={locationInvalid ? 'booking-delivery-location-error' : undefined}
            onChange={(event) => onLocationChange(event.target.value)} />
          {locationInvalid && locationTouched && (
            <span id="booking-delivery-location-error" className={s.error} role="alert">
              {formatMessage(messages.boErrDeliveryLocation)}
            </span>
          )}
        </div>
      )}
      <p className={s.note}>
        <span className="glyphicon glyphicon-info-sign" aria-hidden="true" /> {formatMessage(messages.boProvidedByOwner)}
      </p>
    </section>
  );
}

BookingOptions.propTypes = {
  options: PropTypes.array,
  selectedIds: PropTypes.array,
  onToggle: PropTypes.func.isRequired,
  deliveryLocation: PropTypes.string,
  onLocationChange: PropTypes.func.isRequired,
  currency: PropTypes.string,
  disabled: PropTypes.bool,
};

export default withStyles(s)(BookingOptions);
